import { v4 as UUIDv4 } from 'uuid';
import { request, requestModelVersionDetail } from '@shakkerai/services';
import { isOverseas } from '@shakkerai/pro-utils';
import { deleteCookie, getCookie } from '@shakkerai/util-cookie';
import { getUnitData } from '@starflow/app/helpers/flow';
import { setTempState, getTempState } from '@starflow/app/store';
import { getPartialState, updateLoraSegState, updatePartialState, useCategories } from './state';
import { config } from '@shakkerai/config';
import { presetCheckpointsMap } from '../constants';

export const modelTypeMap = {
  checkpoint: '1',
  lora: '5',
  comfy: '18',
};

export const modelTypeLabelsMap = {
  1: 'checkpoint',
  5: 'lora',
  18: 'comfy',
};
const urlPix = config.proxyUrl ? config.proxyUrl : '';
const endpointForQueryType = {
  all: `${urlPix}/api/www/model/tool/baseSearch`,
  history: `${urlPix}/gateway/sd-api/gen/tool/model/used/history`,
  favorites: `${urlPix}/api/www/model/tool/like/list`,
};

const cachedRequestIds = {};

const mapFilterParams = (filters) => {
  const params = {
    combinedTagCodes: filters.combinedTagCodes,
    page: filters.page || 1,
    pageSize: filters.pageSize || 30,
  };

  const requestIdCacheKey = `${filters.modelType}-${filters.queryType}`;

  if (!cachedRequestIds[requestIdCacheKey] || params.page === 1) {
    cachedRequestIds[requestIdCacheKey] = UUIDv4();
  }

  params.requestId = cachedRequestIds[requestIdCacheKey];

  if (filters.modelType) {
    params.modelType = modelTypeMap[filters.modelType];
  }
  if (filters.keyword && filters.queryType !== 'favorites') {
    params.query = filters.keyword.trim();
  }

  if (filters.sortType) {
    params.sort = Number(filters.sortType);
  }

  if (filters.baseTypes?.length) {
    params.baseTypes = filters.baseTypes.filter(Boolean);
  }

  return params;
};

export const requestSuggestedKeywords = async (referenceModels) => {
  try {
    const result = await request(`${urlPix}/api/www/model/tool/listSuggestedKeywords`, { referenceModels });
    return result.data || [];
  } catch {
    return [];
  }
};

export const reportModelUsage = async (versionData) => {
  const modelTypeString = versionData.modelType?.toLowerCase();
  const modelTypeNumber = modelTypeMap[modelTypeString];

  if (!modelTypeNumber) return;

  try {
    const result = await request(`${urlPix}/gateway/sd-api/gen/tool/model/used/history/add`, {
      versionId: versionData?.versionId,
      versionUuid: versionData.uuid,
      modelType: modelTypeNumber,
    });

    if (result.data === true) {
      updatePartialState(`${modelTypeString}.history.data`, (data) => {
        const { items } = data || {};
        const newItems = items?.filter((item) => item.versionId !== versionData?.versionId);
        return {
          items: [versionData].concat(newItems),
        };
      });
    }
  } catch (error) {
    console.log('error', error);
  }
};

export const watchRecentFavoriteModels = () => {
  const appendRecentFavoriteModel = async (versionUuid, showTip) => {
    try {
      localStorage.removeItem('shakker_newly_favorited_model');
      deleteCookie('pic_newly_favorited_model');

      const modelDetail = await requestModelVersionDetail(versionUuid).then((res) => res.data);
      if (!modelDetail?.uuid) return;

      const modelType = modelDetail.modelType?.toLowerCase();
      if (modelType !== 'checkpoint' && modelType !== 'lora') return;

      modelDetail.isNewlyFavorited = true;

      if (modelDetail.executionScope) {
        try {
          const executionScope = JSON.parse(modelDetail.executionScope);
          modelDetail.unsupported = !executionScope.scopes['webui'];
        } catch {
          // ...
        }
      }

      if (showTip && !modelDetail.unsupported) {
        setTempState({ newlyFavoritedModel: modelDetail });
      }

      updatePartialState(`${modelType}.favorites.data`, (data) => {
        return {
          items: [modelDetail].concat(data.items),
        };
      });
    } catch (error) {
      console.log('[DEBUG]', error);
    }
  };

  const handleStrorageChange = async (event) => {
    if (event.key === 'shakker_newly_favorited_model') {
      event.newValue && appendRecentFavoriteModel(event.newValue, true);
    }
  };

  const handleCookieChange = async ({ changed }) => {
    for (const { name, value } of changed) {
      if (name === 'pic_newly_favorited_model') {
        appendRecentFavoriteModel(value, true);
        return;
      }
    }
  };

  window.addEventListener('storage', handleStrorageChange);

  if (!isOverseas && window.cookieStore) {
    // 国内环境下，使用cookie
    window.cookieStore.addEventListener('change', handleCookieChange);
  }

  const versionUuidFromLocalStorage = localStorage.getItem('shakker_newly_favorited_model');
  const versionUuidFromCookie = getCookie('pic_newly_favorited_model');

  if (versionUuidFromLocalStorage || versionUuidFromCookie) {
    appendRecentFavoriteModel(versionUuidFromLocalStorage || versionUuidFromCookie);
  }
};

export const queryCategories = async (str) => {
  try {
    const result = await request(`${urlPix}/api/canva/public/tag/list?category=${str}`);
    if (result.data?.[str]?.length) {
      const {
        data: { categoryAliases, featuredCates, ...rest },
      } = result;
      categoryAliases.forEach((o) => {
        const codes = (o.includedTags ?? '').split(',');
        const newO = { ...o, codes };
        const l = rest[str].find((lo) => newO.categoryCode === lo.code);
        if (l) l.level2 ? l.level2.push(newO) : (l.level2 = [newO]);
      });
      const state = {
        [str]: {
          catalogArr: rest[str],
          featuredCates: featuredCates.map((p) => ({ ...p, codes: (p.includedTags ?? '').split(',') })),
        },
      };
      useCategories.setState(state);
      return state;
    } else {
      return {};
    }
  } catch (error) {
    // ...
    return {};
  }
};

export const queryRelatedLoraModels = async () => {
  const { prompt } = getUnitData();

  if (prompt?.length) {
    request(`${urlPix}/api/www/model/tool/baseSearch`, {
      modelType: '5',
      genPrompt: prompt,
      page: 1,
      pageSize: 5,
    }).then((res) => {
      if (Array.isArray(res.data?.data)) {
        updatePartialState('lora.all', {
          promptRelatedItems: res.data.data.map((item) => {
            item.isPromptRelated = true;
            return item;
          }),
        });
      }
    });
  }
};

export const updateModelItem = (modelData = {}, partialData) => {
  const modelType = modelData.modelType?.toLowerCase();

  if (modelType === 'lora' || modelType === 'checkpoint') {
    // 更新所有的loraModels
    updatePartialState(modelType, (data) => {
      const { favorites, history, all } = data;

      // 如果favorites没包含这一项，
      let favoriteItems = favorites.data.items;

      if (partialData.isLike && !favoriteItems.find((item) => item.versionId === modelData.versionId)) {
        favoriteItems = favoriteItems.concat(modelData);
      } else {
        favoriteItems = favoriteItems.map((item) => {
          return item.versionId === modelData.versionId ? Object.assign({}, item, partialData) : item;
        });
      }

      const historyItems = history.data.items.map((item) => {
        return item.versionId === modelData.versionId ? Object.assign({}, item, partialData) : item;
      });

      const allItems = all.data.items?.map((item) => {
        return item.versionId === modelData.versionId ? Object.assign({}, item, partialData) : item;
      });

      return {
        favorites: {
          ...favorites,
          data: {
            ...favorites.data,
            items: favoriteItems,
          },
        },
        history: {
          ...history,
          data: {
            ...history.data,
            items: historyItems,
          },
        },
        all: {
          ...all,
          data: {
            ...all.data,
            items: allItems,
          },
        },
      };
    });
  }
};

export const getTagCodesV2 = async () => {
  const { checkpoint, presetBaseModelId } = getUnitData();
  // base_checkpoint(code === 102000) 这个类型就不用去获取tagV2Ids
  let uuid = '';
  if (presetBaseModelId === 'base_checkpoint') {
    return [];
  }
  if (presetBaseModelId && presetCheckpointsMap[presetBaseModelId]) {
    // 预设
    uuid = presetCheckpointsMap[presetBaseModelId].versionDetail.uuid;
  } else {
    // ckpt
    uuid = checkpoint.uuid;
  }
  if (uuid) {
    try {
      const res = await requestModelVersionDetail(uuid);
      if (res.code === 0) {
        // 拉取回来的预设模型tagV2Ids有可能包含102000（base_checkpoint），会影响他对应垂类的标签，所以把base_checkpoint过滤掉
        return res.data.tagV2Ids.filter((item) => item !== 102000).map((item) => `${item}`);
      } else {
        return [];
      }
    } catch (err) {
      console.log('api err', err);
      return [];
    }
  } else {
    return [];
  }
};

export const queryLoraSegState = async (statePath, param, featuredCate) => {
  try {
    const modelType = 'lora';
    const queryType = 'all';
    const params = mapFilterParams({ ...param, modelType, queryType });

    if (param.category !== '0') {
      params.tagCode = param.category;
    }
    if (params.query) {
      delete params.sort;
    }
    const res = await request('/api/www/model/tool/baseSearch', params);
    if (res.code === 0) {
      const data = res.data.data;
      updateLoraSegState(statePath, data, featuredCate);
    } else {
      console.log('api error');
    }
  } catch (err) {
    console.log('api error', err);
  }
};

export const queryModelState = async (modelType, queryType, extraParams = {}, isInitialize) => {
  const statePath = `${modelType}.${queryType}`;
  const { filters, data } = getPartialState(statePath);

  if (data.loading && filters.page > 1) return;
  if (isInitialize && data.items.length) return;
  const storeTarget = extraParams.tagCode ? `${statePath}.data.${extraParams.tagCode}` : `${statePath}.data`;

  updatePartialState(storeTarget, { loading: true });
  try {
    const params = mapFilterParams({ ...filters, modelType, queryType });
    if (filters.category !== '0') {
      params.tagCode = filters.category;
    }
    const ps = { ...params, ...extraParams };
    if (ps.query) {
      delete ps.sort;
    }
    const result = await request(endpointForQueryType[queryType], ps);

    const items = result.data.list || result.data.data || result.data || [];
    const newlyFavoritedModel = getTempState('newlyFavoritedModel');

    if (!Array.isArray(items)) {
      return updatePartialState(storeTarget, { loading: false });
    }

    items.forEach((item) => {
      if (queryType === 'favorites') {
        item.isLike = 1;
        if (newlyFavoritedModel && newlyFavoritedModel.uuid === item.uuid) {
          item.isNewlyFavorited = true;
        }
      }

      if (item.executionScope) {
        try {
          const executionScope = JSON.parse(item.executionScope);
          item.unsupported = !executionScope.scopes['webui'];
        } catch {
          // ...
        }
      }

      item.tags = item.tagsV2?.modelContent?.map((tag) => ({
        code: tag.id + '',
        tagLabel: tag.tagLabel,
        level: tag.level,
      }));

      item.tagsString = item.tags?.map((tag) => tag.tagLabel).join(',');
    });

    if (queryType === 'favorites') {
      return updatePartialState(storeTarget, {
        items: items,
        total: items.length,
        loading: false,
        hasMore: false,
      });
    }

    const { page, pageSize, total } = result.data;
    const hasMore =
      typeof result.data.hasMore === 'boolean'
        ? result.data.hasMore
        : items.length === pageSize && page * pageSize < total;

    if (filters.page === 1) {
      updatePartialState(storeTarget, {
        items: items,
        total: total,
        hasMore,
        loading: false,
      });
    } else {
      updatePartialState(storeTarget, (data) => ({
        items: data.items
          .concat(items)
          .filter((item, index, array) => array.findIndex((i) => i.versionId === item.versionId) === index),
        total: total,
        hasMore,
        loading: false,
      }));
    }
  } catch (error) {
    console.log('[DEBUG] error', error);
  } finally {
    updatePartialState(storeTarget, { loading: false });
  }
};
